import { type FormArray, type FormGroup } from '@angular/forms';

import { type DatasetInfo } from '@blaze-shared/api-contracts-domain';
import {
  type ConnectorConfiguration,
  type ConnectorTemplate,
  type EditedConnectorConfiguration,
  type FormConfig,
  type FormValues,
} from '@blaze-shared/connectors-domain';

export function isRestApiConnector(
  connector?: ConnectorTemplate | ConnectorConfiguration,
): boolean {
  return connector?.connectorType === 'CustomApi';
}

export function prepareConnectorConfiguration(
  connectorName: string,
  templateId: string | undefined,
  accountId: string | undefined,
  existingConnector: ConnectorConfiguration | undefined,
  configuration?: FormValues,
): ConnectorConfiguration {
  return {
    ...existingConnector,
    accountId: accountId || '',
    actions: existingConnector?.actions || [],
    configuration: { ...configuration },
    datasets: existingConnector?.datasets || [],
    name: connectorName,
    templateId: templateId || '',
  };
}

export function prepareEditedConnectorConfiguration(
  formConfig: FormConfig,
  formValues: FormValues,
  datasets: DatasetInfo[] | undefined = [],
): EditedConnectorConfiguration {
  const connectorName = formValues?.name as string;

  if (!connectorName) {
    throw new Error('Connector must have a name');
  }

  // Strip empty secrets
  Object.keys(formValues).forEach((key) => {
    const value = formValues[key];
    const isSecret = formConfig.find((field) => field.name === key)?.isSecret;

    if (!value && isSecret) {
      delete formValues[key];
    }
  });

  return {
    name: connectorName,
    configuration: formValues,
    datasets,
  };
}

export function updateFormArrayControlsLengthMutably<T extends FormArray>(
  control: T,
  expectedControlsLength: number,
  newControlFactory: (control: T) => FormGroup,
  minControlsLength = 1,
): void {
  const controlLength = control.length;

  let delta =
    expectedControlsLength >= minControlsLength ? controlLength - expectedControlsLength : 0;
  if (delta < 0) {
    delta = Math.abs(delta);
    for (let i = 0; i < delta; i += 1) {
      control.push(newControlFactory(control));
    }
  } else if (delta > 0) {
    for (let i = delta; i >= expectedControlsLength; i -= 1) {
      control.removeAt(i);
    }
  }
}
